import { useState } from 'react';
import { clsx } from 'clsx';
import { getFeats, getFeat, meetsFeatPrerequisites } from '@character-creator/data';
import type { AbilityScores, AbilityName, FeatDefinition } from '@character-creator/types';
import type { AsiChoice } from '../../stores/characterCreationStore';

const ABILITIES: { id: AbilityName; name: string }[] = [
  { id: 'strength', name: 'Strength' },
  { id: 'dexterity', name: 'Dexterity' },
  { id: 'constitution', name: 'Constitution' },
  { id: 'intelligence', name: 'Intelligence' },
  { id: 'wisdom', name: 'Wisdom' },
  { id: 'charisma', name: 'Charisma' },
];

interface AsiSelectorProps {
  asiLevel: number;
  currentChoice: AsiChoice | null;
  abilityScores: AbilityScores;
  characterLevel: number;
  onSelect: (choice: AsiChoice | null) => void;
  compact?: boolean;
}

type AsiMode = 'plus-two' | 'plus-one-twice' | 'feat';

export function AsiSelector({
  asiLevel,
  currentChoice,
  abilityScores,
  characterLevel,
  onSelect,
  compact = false,
}: AsiSelectorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [mode, setMode] = useState<AsiMode | null>(
    currentChoice?.type === 'feat' ? 'feat' :
    currentChoice?.type === 'ability-increase' ?
      (currentChoice.increases.length === 1 ? 'plus-two' : 'plus-one-twice') :
      null
  );
  const [selectedAbility1, setSelectedAbility1] = useState<AbilityName | null>(
    currentChoice?.type === 'ability-increase' ? currentChoice.increases[0]?.ability ?? null : null
  );
  const [selectedAbility2, setSelectedAbility2] = useState<AbilityName | null>(
    currentChoice?.type === 'ability-increase' && currentChoice.increases.length > 1
      ? currentChoice.increases[1]?.ability ?? null
      : null
  );

  const feats = getFeats();

  // Get the description of the current choice for compact view
  const getChoiceDescription = (): string => {
    if (!currentChoice) return '';
    if (currentChoice.type === 'feat') {
      const feat = getFeat(currentChoice.featId);
      return `Feat: ${feat?.name ?? currentChoice.featId}`;
    }
    const increases = currentChoice.increases
      .map(inc => `+${inc.amount} ${inc.ability.charAt(0).toUpperCase() + inc.ability.slice(1)}`)
      .join(', ');
    return increases;
  };

  // Handle change button click - reset state and clear choice
  const handleChange = () => {
    setIsEditing(true);
    setMode(null);
    setSelectedAbility1(null);
    setSelectedAbility2(null);
    onSelect(null);
  };

  // Compact view - shows selected choice with change option
  // Don't show compact view if user is actively editing
  if (compact && currentChoice && !isEditing) {
    return (
      <div className="text-xs text-success flex items-center gap-1">
        <span>Level {asiLevel} ASI: {getChoiceDescription()}</span>
        <button
          type="button"
          onClick={handleChange}
          className="ml-2 text-text-muted hover:text-error transition-colors"
        >
          (change)
        </button>
      </div>
    );
  }

  // Check if an ability can be increased (max 20)
  const canIncreaseAbility = (ability: AbilityName, amount: number): boolean => {
    return abilityScores[ability] + amount <= 20;
  };

  // Handle ability selection for +2 mode
  const handlePlusTwoSelect = (ability: AbilityName) => {
    if (!canIncreaseAbility(ability, 2)) return;
    setSelectedAbility1(ability);
    setSelectedAbility2(null);
    setIsEditing(false);
    onSelect({
      type: 'ability-increase',
      increases: [{ ability, amount: 2 }],
    });
  };

  // Handle ability selection for +1 twice mode
  const handlePlusOneSelect = (ability: AbilityName, slot: 1 | 2) => {
    if (!canIncreaseAbility(ability, 1)) return;

    if (slot === 1) {
      setSelectedAbility1(ability);
      if (selectedAbility2) {
        setIsEditing(false);
        onSelect({
          type: 'ability-increase',
          increases: [
            { ability, amount: 1 },
            { ability: selectedAbility2, amount: 1 },
          ],
        });
      }
    } else {
      setSelectedAbility2(ability);
      if (selectedAbility1) {
        setIsEditing(false);
        onSelect({
          type: 'ability-increase',
          increases: [
            { ability: selectedAbility1, amount: 1 },
            { ability, amount: 1 },
          ],
        });
      }
    }
  };

  // Handle feat selection
  const handleFeatSelect = (featId: string) => {
    setIsEditing(false);
    onSelect({ type: 'feat', featId });
  };

  return (
    <div>
      <h5 className="text-sm font-medium text-text-primary mb-2">
        Level {asiLevel} - Ability Score Improvement
        {!currentChoice && (
          <span className="text-warning ml-2 text-xs">(Required)</span>
        )}
      </h5>
      <p className="text-xs text-text-muted mb-3">
        You can increase one ability score by 2, or two ability scores by 1 each, or choose a feat.
      </p>

      {/* Mode selection */}
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => {
            setMode('plus-two');
            setSelectedAbility1(null);
            setSelectedAbility2(null);
            onSelect(null);
          }}
          className={clsx(
            'flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition-all',
            mode === 'plus-two'
              ? 'bg-accent-gold/10 border-accent-gold text-accent-gold'
              : 'bg-background-secondary border-background-elevated text-text-secondary hover:border-text-muted'
          )}
        >
          +2 to One
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('plus-one-twice');
            setSelectedAbility1(null);
            setSelectedAbility2(null);
            onSelect(null);
          }}
          className={clsx(
            'flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition-all',
            mode === 'plus-one-twice'
              ? 'bg-accent-gold/10 border-accent-gold text-accent-gold'
              : 'bg-background-secondary border-background-elevated text-text-secondary hover:border-text-muted'
          )}
        >
          +1 to Two
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('feat');
            setSelectedAbility1(null);
            setSelectedAbility2(null);
            onSelect(null);
          }}
          className={clsx(
            'flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition-all',
            mode === 'feat'
              ? 'bg-accent-gold/10 border-accent-gold text-accent-gold'
              : 'bg-background-secondary border-background-elevated text-text-secondary hover:border-text-muted'
          )}
        >
          Feat
        </button>
      </div>

      {/* +2 to one ability */}
      {mode === 'plus-two' && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {ABILITIES.map((ability) => {
            const canIncrease = canIncreaseAbility(ability.id, 2);
            const isSelected = selectedAbility1 === ability.id;
            return (
              <button
                key={ability.id}
                type="button"
                onClick={() => handlePlusTwoSelect(ability.id)}
                disabled={!canIncrease}
                className={clsx(
                  'p-3 rounded-lg border text-left transition-all duration-200',
                  isSelected
                    ? 'bg-accent-gold/10 border-accent-gold'
                    : canIncrease
                    ? 'bg-background-secondary border-background-elevated hover:border-text-muted'
                    : 'bg-background-secondary border-background-elevated opacity-50 cursor-not-allowed'
                )}
              >
                <div className="font-medium text-text-primary text-sm">{ability.name}</div>
                <div className="text-xs text-text-muted">
                  {abilityScores[ability.id]} → {abilityScores[ability.id] + 2}
                  {!canIncrease && ' (max 20)'}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* +1 to two abilities */}
      {mode === 'plus-one-twice' && (
        <div className="space-y-4">
          <div>
            <p className="text-xs text-text-secondary mb-2">First ability (+1):</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {ABILITIES.map((ability) => {
                const canIncrease = canIncreaseAbility(ability.id, 1);
                const isSelected = selectedAbility1 === ability.id;
                const isDisabled = !canIncrease || selectedAbility2 === ability.id;
                return (
                  <button
                    key={ability.id}
                    type="button"
                    onClick={() => handlePlusOneSelect(ability.id, 1)}
                    disabled={isDisabled}
                    className={clsx(
                      'p-2 rounded-lg border text-left transition-all duration-200',
                      isSelected
                        ? 'bg-accent-gold/10 border-accent-gold'
                        : !isDisabled
                        ? 'bg-background-secondary border-background-elevated hover:border-text-muted'
                        : 'bg-background-secondary border-background-elevated opacity-50 cursor-not-allowed'
                    )}
                  >
                    <div className="font-medium text-text-primary text-xs">{ability.name}</div>
                    <div className="text-xs text-text-muted">
                      {abilityScores[ability.id]} → {abilityScores[ability.id] + 1}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="text-xs text-text-secondary mb-2">Second ability (+1):</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {ABILITIES.map((ability) => {
                const canIncrease = canIncreaseAbility(ability.id, 1);
                const isSelected = selectedAbility2 === ability.id;
                const isDisabled = !canIncrease || selectedAbility1 === ability.id;
                return (
                  <button
                    key={ability.id}
                    type="button"
                    onClick={() => handlePlusOneSelect(ability.id, 2)}
                    disabled={isDisabled}
                    className={clsx(
                      'p-2 rounded-lg border text-left transition-all duration-200',
                      isSelected
                        ? 'bg-accent-gold/10 border-accent-gold'
                        : !isDisabled
                        ? 'bg-background-secondary border-background-elevated hover:border-text-muted'
                        : 'bg-background-secondary border-background-elevated opacity-50 cursor-not-allowed'
                    )}
                  >
                    <div className="font-medium text-text-primary text-xs">{ability.name}</div>
                    <div className="text-xs text-text-muted">
                      {abilityScores[ability.id]} → {abilityScores[ability.id] + 1}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Feat selection */}
      {mode === 'feat' && (
        <div className="space-y-2">
          {/* Hint when ability scores haven't been assigned */}
          {Object.values(abilityScores).every((s) => s === 8) && (
            <p className="text-xs text-accent-gold bg-accent-gold/10 p-2 rounded-lg">
              Browse feats to plan your build. Prerequisites will update as you assign ability scores.
            </p>
          )}
          {feats.length === 0 ? (
            <p className="text-xs text-text-muted italic">No feats available in current ruleset.</p>
          ) : (
            feats.map((feat: FeatDefinition) => {
              const prereqResult = meetsFeatPrerequisites(feat, {
                abilityScores: abilityScores as unknown as Record<string, number>,
                level: characterLevel,
              });
              const isSelected = currentChoice?.type === 'feat' && currentChoice.featId === feat.id;
              return (
                <div
                  key={feat.id}
                  onClick={() => prereqResult.meets && handleFeatSelect(feat.id)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && prereqResult.meets) {
                      e.preventDefault();
                      handleFeatSelect(feat.id);
                    }
                  }}
                  className={clsx(
                    'w-full text-left p-3 rounded-lg border transition-all duration-200',
                    isSelected
                      ? 'bg-accent-gold/10 border-accent-gold cursor-pointer'
                      : prereqResult.meets
                      ? 'bg-background-secondary border-background-elevated hover:border-text-muted cursor-pointer'
                      : 'bg-background-secondary border-background-elevated'
                  )}
                  role="button"
                  tabIndex={prereqResult.meets ? 0 : -1}
                  aria-disabled={!prereqResult.meets}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-text-primary text-sm">{feat.name}</span>
                    {!prereqResult.meets ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-error/20 text-error flex-shrink-0">
                        {prereqResult.reason}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded bg-success/20 text-success flex-shrink-0">
                        Qualified
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-secondary mt-1">{feat.description}</p>
                  <ul className="mt-2 text-xs text-text-muted list-disc list-inside">
                    {feat.benefits.map((benefit: string, i: number) => (
                      <li key={i}>{benefit}</li>
                    ))}
                  </ul>
                  {!prereqResult.meets && (
                    <p className="mt-2 text-xs text-text-muted italic">
                      Assign ability scores to unlock this feat
                    </p>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
